// @ts-nocheck
import { useState, useCallback } from 'react'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { API_BASE } from '@/lib/api'

export function MessageFeedback({ sessionId, messageId, agent }) {
  const [rating, setRating] = useState(null)
  const [comment, setComment] = useState('')
  const [commentOpen, setCommentOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const saveFeedback = useCallback(async (newRating, feedbackComment) => {
    setSaving(true)
    try {
      await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('pharmax_token') || ''}`,
        },
        body: JSON.stringify({
          type: 'message_feedback',
          severity: newRating === 'positive' ? 'low' : 'medium',
          message: feedbackComment || `${newRating} feedback on message`,
          context: { session_id: sessionId, message_id: messageId, agent, rating: newRating },
        }),
      })
      toast.success('Thanks for the feedback')
    } catch {
      // silently fail
    } finally {
      setSaving(false)
    }
  }, [sessionId, messageId, agent])

  const handleThumbsUp = useCallback(() => {
    const newRating = rating === 'positive' ? null : 'positive'
    setRating(newRating)
    if (newRating === 'positive') saveFeedback('positive')
    setCommentOpen(false)
  }, [rating, saveFeedback])

  const handleThumbsDown = useCallback(() => {
    if (rating === 'negative') { setRating(null); setCommentOpen(false); return }
    setRating('negative')
    setCommentOpen(true)
  }, [rating])

  const submitComment = useCallback(() => {
    saveFeedback('negative', comment)
    setCommentOpen(false)
  }, [comment, saveFeedback])

  return (
    <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`feedback-${messageId}`}>
      <Button
        variant="ghost" size="icon"
        className={cn('h-6 w-6 rounded-md',
          rating === 'positive' ? 'text-emerald-400 bg-emerald-400/10' : 'text-muted-foreground/40 hover:text-muted-foreground'
        )}
        onClick={handleThumbsUp} disabled={saving}
        data-testid={`thumbs-up-${messageId}`}
      >
        <ThumbsUp className="h-3 w-3" fill={rating === 'positive' ? 'currentColor' : 'none'} />
      </Button>

      <Popover open={commentOpen} onOpenChange={setCommentOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost" size="icon"
            className={cn('h-6 w-6 rounded-md',
              rating === 'negative' ? 'text-red-400 bg-red-400/10' : 'text-muted-foreground/40 hover:text-muted-foreground'
            )}
            onClick={handleThumbsDown} disabled={saving}
            data-testid={`thumbs-down-${messageId}`}
          >
            <ThumbsDown className="h-3 w-3" fill={rating === 'negative' ? 'currentColor' : 'none'} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="end" data-testid={`feedback-popover-${messageId}`}>
          <p className="text-xs font-medium mb-2 text-muted-foreground">What went wrong?</p>
          <Textarea
            value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Optional feedback..." className="min-h-[60px] text-xs resize-none"
          />
          <Button size="sm" className="mt-2 w-full h-7 text-xs" onClick={submitComment} disabled={saving}>
            Submit
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  )
}
